import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { colocService, type PublicationReadiness } from '../services/coloc.service';
import { authService } from '../services/auth.service';
import { urlSitePublic, libelleSitePublic } from '../utils/publicUrl';
import { DEV } from '../utils/devFixtures';
import { remplirPourPublication } from '../utils/devPublication';
import { useCapabilities } from '../contexts/CapabilitiesContext';
import { isHttpError } from '../utils/error';
import type { Coloc } from '../types/coloc';
import './PublicationCard.css';

/**
 * État de publication du site public — affiché sur le tableau de bord.
 *
 * C'est la surface de conversion (saas/12) : sans carte, le tableau de bord
 * est DÉGRADÉ, pas bloqué. La carte montre le site prêt à publier et envoie
 * vers l'écran d'abonnement ; avec un plan actif, elle publie/dépublie.
 *
 * Ordre des états, délibéré : COMPLÉTUDE, puis EMAIL, puis PAIEMENT. Proposer
 * « Publier » à un bailleur dont la fiche est vide lui vendrait une page
 * publique fausse — carte au large de l'Afrique, galerie vide, blocs sans
 * corps. On lui dit donc ce qu'il lui reste à faire avant de lui parler
 * d'argent.
 *
 * L'email s'intercale entre les deux : `CapabilityMiddleware` refuse
 * `publish_site` tant que l'adresse n'est pas confirmée, AVANT même d'évaluer
 * le plan. Sans cette étape ici, le bailleur payait puis butait sur la
 * vérification — une carte encaissée pour une action qui échoue quand même.
 */
const PublicationCard: React.FC<{ coloc: Coloc }> = ({ coloc }) => {
  const navigate = useNavigate();
  const { stateOf, loaded, data, refresh } = useCapabilities();
  const [isPublished, setIsPublished] = useState<boolean | null>(null);
  const [readiness, setReadiness] = useState<PublicationReadiness | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const [remplissage, setRemplissage] = useState(false);
  // Publication refusée au-delà du quota du palier : le changement d'offre se
  // fait par « Gérer mon abonnement », dans Mon compte.
  const [quotaAtteint, setQuotaAtteint] = useState(false);

  useEffect(() => {
    // La coloc sélectionnée vient du localStorage : on relit l'état publié
    // depuis l'API plutôt que de faire confiance à une copie périmée.
    colocService
      .getColocs()
      .then((list) => setIsPublished(!!list.find((c) => c.id === coloc.id)?.isPublished))
      .catch(() => setIsPublished(null));
    colocService
      .publicationReadiness(coloc.id)
      .then(setReadiness)
      // En cas d'échec on n'invente rien : l'API refusera la publication si
      // la fiche est incomplète, avec la liste dans sa réponse.
      .catch(() => setReadiness(null));
  }, [coloc.id]);

  if (!loaded || isPublished === null) return null;

  const publishState = stateOf('publish_site');
  const publicUrl = urlSitePublic(coloc.slug);
  const libelleUrl = libelleSitePublic(coloc.slug);
  const manques = readiness && !readiness.ready ? readiness.missing : [];

  /**
   * Raccourci de DÉVELOPPEMENT : remplit tout ce qui bloque la publication.
   * Traverser le parcours à la main — six champs, une chambre, deux photos —
   * coûte plusieurs minutes à chaque compte de test, et le compte de test est
   * jetable par construction.
   *
   * `DEV` est faux dans un build de production : le bouton n'y est jamais
   * rendu, donc ce chemin est inatteignable en ligne. Il n'est pas pour autant
   * ÉLIMINÉ du bundle — vérifié, la configuration CRA ne replie pas cette
   * constante — mais il n'appelle que des routes déjà accessibles au
   * propriétaire authentifié : aucun privilège n'est exposé.
   */
  const remplirAutomatiquement = async () => {
    setRemplissage(true);
    setError(null);
    try {
      const { ignores } = await remplirPourPublication(coloc.id, manques);
      const suite = await colocService.publicationReadiness(coloc.id);
      setReadiness(suite);
      if (ignores.length > 0) {
        setError(`Manques non couverts par le remplissage : ${ignores.join(', ')}.`);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Remplissage impossible.');
    } finally {
      setRemplissage(false);
    }
  };
  // `=== false` et non `!` : catalogue indisponible (`data` null) signifie
  // inconnu, pas « non vérifié ». Le contexte échoue ouvert par conception —
  // inventer un blocage sur une panne réseau serait pire que de laisser
  // l'API refuser d'elle-même.
  const emailAConfirmer = data?.emailVerified === false;

  /**
   * DÉVELOPPEMENT : confirme l'adresse sans passer par l'email.
   *
   * Le jeton reste le VRAI et il est consommé par `/auth/verify-email`, le même
   * endpoint que la page ouverte depuis un courrier : on raccourcit l'attente,
   * pas le parcours. Rien n'est marqué vérifié en base par un chemin dérobé.
   *
   * Une première version ouvrait le lien dans un onglet. Les navigateurs
   * bloquent cette fenêtre — l'adresse finissait confirmée sans que rien ne
   * s'affiche, ce qui donnait un bouton qui semble ne rien faire. On consomme
   * donc le jeton ici, et on le dit.
   */
  const confirmerAdresseEnDev = async () => {
    setResending(true);
    setResendMsg(null);
    try {
      const { alreadyVerified, url } = await authService.devVerificationLink();
      if (alreadyVerified) {
        setResendMsg('Adresse déjà confirmée — rechargez la page.');
      } else if (url) {
        const jeton = new URL(url).searchParams.get('token');
        if (!jeton) throw new Error('lien sans jeton');
        await authService.verifyEmail(jeton);
        authService.markEmailVerifiedLocally();
        setResendMsg('Adresse confirmée.');
      }
      // `emailVerified` vient du catalogue des capacités, chargé une seule fois
      // par session : sans ce rafraîchissement, la carte resterait figée.
      await refresh();
    } catch {
      setResendMsg("Échec — l'API n'est peut-être pas en mode développement.");
    } finally {
      setResending(false);
    }
  };

  const renvoyerVerification = async () => {
    setResending(true);
    setResendMsg(null);
    try {
      const r = await authService.resendVerification();
      setResendMsg(
        r.alreadyVerified
          ? 'Adresse déjà confirmée — rechargez la page.'
          : r.success
            ? 'Email envoyé. Pensez à regarder dans vos indésirables.'
            : "L'envoi a échoué. Réessayez dans quelques minutes.",
      );
    } catch {
      setResendMsg("L'envoi a échoué. Réessayez.");
    } finally {
      setResending(false);
    }
  };

  const toggle = async () => {
    setBusy(true);
    setError(null);
    setQuotaAtteint(false);
    try {
      if (isPublished) {
        await colocService.unpublish(coloc.id);
        setIsPublished(false);
      } else {
        await colocService.publish(coloc.id);
        setIsPublished(true);
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      // Le 409 de complétude porte sa liste : on la réaffiche plutôt que
      // d'afficher un message générique.
      if (detail?.missing) {
        setReadiness({ ready: false, missing: detail.missing });
        setError(detail.message);
      } else {
        setError(typeof detail === 'string' ? detail : 'Action impossible pour le moment.');
        // Ce bouton n'existe qu'avec la capacité accordée et l'adresse
        // confirmée : un 403 à la publication vient alors du quota. (Catalogue
        // illisible : le lien mène à Mon compte, sans dommage.)
        setQuotaAtteint(!isPublished && isHttpError(e, 403));
      }
    } finally {
      setBusy(false);
    }
  };

  // 1. Fiche incomplète : ni bouton de publication, ni appel à payer.
  if (!isPublished && manques.length > 0) {
    return (
      <div className="publication-card publication-card--todo">
        <div>
          <h3>Votre site n&apos;est pas encore en ligne</h3>
          <p>
            Il reste {manques.length} élément{manques.length > 1 ? 's' : ''} à
            renseigner avant de pouvoir publier{' '}
            <strong>{libelleUrl}</strong>.
          </p>
          <ul className="publication-todo">
            {manques.map((m) => (
              <li key={m.code}>
                <span className="publication-todo-label">{m.label}</span>
                <button
                  type="button"
                  className="publication-todo-cta"
                  // Le manque de photos ouvre directement l'onglet Photos :
                  // atterrir sur le formulaire pour ensuite chercher l'onglet
                  // ferait de ce bouton une demi-réponse.
                  onClick={() =>
                    navigate(
                      m.where === 'rooms'
                        ? '/rooms'
                        : m.code === 'photos'
                          ? '/details?onglet=photos'
                          : '/details',
                    )
                  }
                >
                  {m.where === 'rooms' ? 'Aux chambres' : 'Compléter'}
                </button>
              </li>
            ))}
          </ul>
          {error && <p className="publication-error">{error}</p>}
          {DEV && (
            <button
              type="button"
              className="publication-devfill"
              onClick={remplirAutomatiquement}
              disabled={remplissage}
            >
              {remplissage
                ? 'Remplissage…'
                : 'Remplir automatiquement tous les champs (dev)'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // 2. Fiche complète mais adresse non confirmée : on ne parle pas encore
  //    d'argent. Passe AVANT l'upsell, sinon la carte serait demandée pour une
  //    publication que le middleware refuserait juste après.
  if (!isPublished && emailAConfirmer) {
    return (
      <div className="publication-card publication-card--email">
        <div>
          <h3>Confirmez votre adresse email</h3>
          <p>
            Dernière étape avant de pouvoir mettre{' '}
            <strong>{libelleUrl}</strong> en ligne. Un
            lien vous a été envoyé à l&apos;inscription.
          </p>
          {resendMsg && <p className="publication-resend-msg">{resendMsg}</p>}
          {DEV && (
            <button
              type="button"
              className="publication-devfill"
              onClick={confirmerAdresseEnDev}
              disabled={resending}
            >
              {resending ? '…' : "Confirmer l'adresse sans email (dev)"}
            </button>
          )}
        </div>
        <button
          className="publication-cta"
          onClick={renvoyerVerification}
          disabled={resending}
        >
          {resending ? '…' : "Renvoyer l'email"}
        </button>
      </div>
    );
  }

  // 3. Fiche complète, adresse confirmée : la publication est la barrière payante.
  if (publishState === 'unlockable' && !isPublished) {
    return (
      <div className="publication-card publication-card--upsell">
        <div>
          <h3>Votre site est prêt à être mis en ligne</h3>
          <p>
            Publiez <strong>{libelleUrl}</strong> pour
            recevoir vos premiers candidats. 14 jours d&apos;essai gratuit à
            l&apos;enregistrement de votre carte.
          </p>
        </div>
        <button className="publication-cta" onClick={() => navigate('/upgrade')}>
          Publier mon site
        </button>
      </div>
    );
  }

  return (
    <div className="publication-card">
      <div>
        <h3>Site public</h3>
        {isPublished ? (
          <p>
            En ligne :{' '}
            <a href={publicUrl} target="_blank" rel="noopener noreferrer">
              {libelleUrl}
            </a>
          </p>
        ) : (
          <p>Non publié — votre site n&apos;est pas visible.</p>
        )}
        {error && <p className="publication-error">{error}</p>}
        {quotaAtteint && (
          <button
            type="button"
            className="publication-todo-cta publication-manage"
            onClick={() => navigate('/my-account')}
          >
            Gérer mon abonnement
          </button>
        )}
      </div>
      {publishState === 'granted' && (
        <button className="publication-cta" onClick={toggle} disabled={busy}>
          {busy ? '…' : isPublished ? 'Dépublier' : 'Publier mon site'}
        </button>
      )}
    </div>
  );
};

export default PublicationCard;
